<template>
  <TransitionGroup
    v-if="isAppPage"
    tag="div"
    aria-live="polite"
    class="flex w-full flex-col items-center gap-3 not-empty:mb-4 sm:items-end"
    enter-from-class="opacity-0 translate-y-1"
    leave-to-class="opacity-0"
    enter-active-class="transition duration-200 motion-reduce:transition-none"
    leave-active-class="transition duration-150 motion-reduce:transition-none"
    @focusin="noteFocusFrom"
  >
    <div
      v-for="toast in toasts"
      :key="toast.id"
      class="pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-lg bg-white p-4 shadow-lg ring-1 ring-purple-200"
      @mouseenter="hold(toast.id, 'pointer')"
      @mouseleave="release(toast.id, 'pointer')"
      @focusin="hold(toast.id, 'focus')"
      @focusout="release(toast.id, 'focus')"
    >
      <Icon
        v-if="toast.failed"
        name="exclamation-circle-solid"
        class="mt-0.5 size-5 flex-none text-red-700"
        aria-hidden="true"
      />
      <span
        v-else
        aria-hidden="true"
        class="mt-0.5 flex size-5 flex-none items-center justify-center rounded-full bg-burgundy-700 text-white"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="3.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="size-3"
        >
          <path d="M5 12.5l4.5 4.5L19 7.5" />
        </svg>
      </span>
      <div class="min-w-0 flex-1">
        <p class="text-sm font-semibold text-gray-900">{{ toast.title }}</p>
        <p class="mt-0.5 text-sm text-gray-600">{{ toast.body }}</p>
        <div
          v-if="toast.next"
          class="mt-3 flex items-center gap-2 border-t border-gray-100 pt-3"
        >
          <p class="min-w-0 flex-1 text-sm text-gray-700">
            <span class="text-gray-500">Next:</span> {{ toast.next.title }}
          </p>
          <button
            type="button"
            class="btn btn-soft btn-sm flex-none"
            :aria-label="`Show me: ${toast.next.title}`"
            @click="startNext(toast)"
          >
            Show me
          </button>
        </div>
        <RouterLink
          v-if="!toast.failed"
          :to="{ name: 'setup-progress' }"
          class="inline-link mt-2 inline-block text-xs font-semibold"
          @click="remove(toast.id)"
        >
          See all
        </RouterLink>
      </div>
      <button
        type="button"
        class="-mt-1 -mr-1 flex size-7 flex-none items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        :aria-label="`Close: ${toast.title}`"
        :data-toast-close="toast.id"
        @click="close(toast.id, $event)"
      >
        <Icon name="times" class="size-4" aria-hidden="true" />
      </button>
    </div>
  </TransitionGroup>
  <LevelUpModal
    :content="modalContent"
    :tour-key="modalTourKey"
    :next-item="modalNext"
    @close="closeModal"
    @start="startFromModal"
  />
</template>

<script setup lang="ts">
import { computed, ref, shallowRef, watch } from "vue";
import { DateTime } from "luxon";
import { useRoute } from "vue-router";
import { useQueryClient } from "@tanstack/vue-query";
import Icon from "@/components/Core/Icon.vue";
import LevelUpModal, {
  type LevelModalContent,
} from "@/components/SetupProgress/LevelUpModal.vue";
import { useGuidedTour, useTourRunning } from "@/composables/useGuidedTour";
import { useSetupChecklist } from "@/composables/useSetupChecklist";
import {
  LEVEL_UP_COPY,
  LEVELS_INTRO_COPY,
  SETUP_ITEM_COPY,
  TOUR_LOAD_FAILED_COPY,
} from "@/copy/setupProgress";
import {
  type SetupItem,
  type SetupLevel,
  type SetupLevelProgress,
  setupProgressKey,
  tourProgressKey,
  useCelebrateLevelMutation,
  useRecheckSetupProgressAfterSaves,
  useSaveTourProgressMutation,
  useSetupProgressQuery,
} from "@/services/setup_progress";
import { useProfileQuery } from "@/services/user";
import { PAGE_TOUR_KEYS } from "@/tours/pageTourKeys";

interface Toast {
  id: number;
  title: string;
  body: string;
  next?: { key: string; title: string };
  failed?: boolean;
}

const TOAST_MS = 6000;
// Not a real tour: its progress row records that levels were introduced.
const LEVELS_INTRO_KEY = "levels-intro";

const toasts = ref<Toast[]>([]);
const timers = new Map<number, ReturnType<typeof setTimeout>>();
const holds = new Map<number, Set<"pointer" | "focus">>();
let nextId = 0;

function push(toast: Omit<Toast, "id">) {
  const id = nextId++;
  toasts.value.push({ ...toast, id });
  resume(id);
}

function remove(id: number) {
  pause(id);
  holds.delete(id);
  toasts.value = toasts.value.filter((toast) => toast.id !== id);
}

let focusReturn: HTMLElement | null = null;

function noteFocusFrom(event: FocusEvent) {
  const from = event.relatedTarget;
  const stack = event.currentTarget;
  if (
    from instanceof HTMLElement &&
    stack instanceof HTMLElement &&
    !stack.contains(from)
  ) {
    focusReturn = from;
  }
}

// From the keyboard, focus would otherwise fall to the body with the fading
// toast. A mouse click leaves focus alone: moving it would hold the next
// toast open.
function close(id: number, event: MouseEvent) {
  if (event.detail === 0) {
    const next = toasts.value.find((toast) => toast.id !== id);
    const target = next
      ? document.querySelector<HTMLElement>(`[data-toast-close="${next.id}"]`)
      : focusReturn?.isConnected
        ? focusReturn
        : null;
    target?.focus();
  }
  remove(id);
}

// A toast with a button stays while the pointer or focus is on it, so it
// doesn't vanish under someone reading it or reaching for "Show me".
function pause(id: number) {
  clearTimeout(timers.get(id));
  timers.delete(id);
}

function resume(id: number) {
  pause(id);
  timers.set(
    id,
    setTimeout(() => remove(id), TOAST_MS),
  );
}

// Pointer and focus each hold it: leaving with one keeps it while the other
// is still there.
function hold(id: number, by: "pointer" | "focus") {
  const reasons = holds.get(id) ?? new Set();
  reasons.add(by);
  holds.set(id, reasons);
  pause(id);
}

function release(id: number, by: "pointer" | "focus") {
  const reasons = holds.get(id);
  reasons?.delete(by);
  if (!reasons?.size) resume(id);
}

const { suggestions } = useSetupChecklist();

function nextSuggestion(): Toast["next"] {
  const item = suggestions.value.find((i) => i.state === "available");
  const copy = item && SETUP_ITEM_COPY[item.key];
  return item && copy ? { key: item.key, title: copy.title } : undefined;
}

function startNext(toast: Toast) {
  if (!toast.next) return;
  remove(toast.id);
  tour.start(toast.next.key);
}

const route = useRoute();
const tour = useGuidedTour();

// Shared dashboards, budgets and reports are public routes, so nothing shows
// there even for a logged-in viewer.
const isAppPage = computed(() =>
  route.matched.some((record) => record.meta.requiresAuth),
);

// The backend keeps reporting a level until the app confirms the celebration,
// so one reached on a public page waits for the next app page.
const levelUp = shallowRef<SetupLevelProgress | null>(null);
const celebrate = useCelebrateLevelMutation();
// Confirmed this session; a refetch landing before the confirmation does
// mustn't bring the modal back.
const celebrated = new Set<SetupLevel>();

function markCelebrated(level: SetupLevel) {
  if (level === "explorer" || celebrated.has(level)) return;
  celebrated.add(level);
  celebrate.mutate(level);
}
const introDismissed = ref(false);
const saveTourProgress = useSaveTourProgressMutation();

const tourRunning = useTourRunning();
const heldToasts: Omit<Toast, "id">[] = [];

const queryClient = useQueryClient();
const progress = useSetupProgressQuery();
useRecheckSetupProgressAfterSaves();
const profile = useProfileQuery();

// Recent sign-ups are welcomed to Fyr; people who were already using it
// when levels launched are introduced to the new feature instead.
const NEW_USER_DAYS = 14;
const isNewUser = computed(() => {
  const joined = profile.data?.dateJoined;
  if (!joined) return false;
  const joinedAt = DateTime.fromISO(String(joined));
  return joinedAt.isValid && -joinedAt.diffNow("days").days <= NEW_USER_DAYS;
});

const modalContent = computed<LevelModalContent | null>(() => {
  const data = progress.data;
  if (!isAppPage.value || !data || !tour.isProgressLoaded()) return null;
  if (tourRunning.value) return null;
  if (!introDismissed.value && !tour.hasSeen(LEVELS_INTRO_KEY)) {
    // Whether to welcome or introduce depends on the profile; waiting for it
    // avoids the title switching under the user. A failed fetch still counts
    // as fetched and gets the standard introduction.
    if (!profile.isFetched) return null;
    const level = data.level.current;
    const welcome = level === "explorer" && isNewUser.value;
    return {
      level,
      title: welcome
        ? LEVELS_INTRO_COPY.welcomeTitle(profile.data?.name?.split(" ")[0])
        : LEVELS_INTRO_COPY.title(level),
      body: welcome
        ? LEVELS_INTRO_COPY.welcomeBody
        : LEVELS_INTRO_COPY.body(level),
      next: LEVELS_INTRO_COPY.next(data.level),
      celebrate: level !== "explorer",
    };
  }
  const reached = levelUp.value?.justReached;
  if (!levelUp.value || !reached || reached === "explorer") return null;
  const copy = LEVEL_UP_COPY[reached];
  return {
    level: reached,
    title: copy.title,
    body: copy.body,
    next: copy.next(levelUp.value),
    celebrate: true,
  };
});
// Page tours never start by themselves, so the first modal offers one.
const modalTourKey = computed(() => {
  if (!modalContent.value || !isIntroPending()) return null;
  const name = route.name;
  return typeof name === "string" ? (PAGE_TOUR_KEYS[name] ?? null) : null;
});
const modalNext = computed(() =>
  modalContent.value && !modalTourKey.value ? (nextSuggestion() ?? null) : null,
);

function isIntroPending(): boolean {
  return !introDismissed.value && !tour.hasSeen(LEVELS_INTRO_KEY);
}

function closeModal() {
  if (isIntroPending()) {
    introDismissed.value = true;
    saveTourProgress.mutate({
      tourKey: LEVELS_INTRO_KEY,
      step: 0,
      status: "completed",
    });
  }
  // The introduction already celebrated the level the user is at.
  const shown =
    levelUp.value?.justReached ?? progress.data?.level.current ?? "explorer";
  markCelebrated(shown);
  levelUp.value = null;
}

function startFromModal(key: string) {
  closeModal();
  tour.start(key);
}

// One at a time: a retry that fails again replaces it.
function showLoadFailed() {
  for (const toast of toasts.value) if (toast.failed) remove(toast.id);
  dropHeldFailures();
  const failed = { ...TOUR_LOAD_FAILED_COPY, failed: true };
  if (toastsHeld.value) heldToasts.push(failed);
  else push(failed);
}

function dropHeldFailures() {
  const kept = heldToasts.filter((toast) => !toast.failed);
  heldToasts.splice(0, heldToasts.length, ...kept);
}

watch(tour.failedLoads, showLoadFailed);

// A tour starting makes an earlier failure old news; if it fails too, it
// says so itself.
watch(tourRunning, (running) => {
  if (!running) return;
  for (const toast of toasts.value) if (toast.failed) remove(toast.id);
  dropHeldFailures();
});

// Under a tour or the level modal a toast would expire unseen, so they wait.
const toastsHeld = computed(() => tourRunning.value || !!modalContent.value);

watch(toastsHeld, (holding) => {
  if (holding) {
    for (const { id, ...toast } of toasts.value) {
      pause(id);
      holds.delete(id);
      heldToasts.push(toast);
    }
    toasts.value = [];
    return;
  }
  if (heldToasts.length === 0) return;
  const held = heldToasts.splice(0);
  held.forEach((toast) => delete toast.next);
  // Suggested now, so it isn't what the tour just walked through.
  const last = held.findLast((toast) => !toast.failed);
  if (last && !levelUp.value) last.next = nextSuggestion();
  held.forEach(push);
});

function doneMessage(item: SetupItem): string {
  const by = item.completedBy;
  if (by && by.id !== profile.data?.id) return `Earned by ${by.name}.`;
  // Personal items are always the user's own; an organization item with no
  // recorded author may be a teammate's, so it isn't congratulated.
  if (item.kind === "organization" && !by) return "Earned.";
  return "Earned. Nice work.";
}
// Items already done when the session first saw an organization are not
// news; after switching, the new organization's done items aren't either.
// Personal items follow the user across organizations, so they're tracked
// once rather than per organization, or a switch back would announce them
// again.
const organizationDoneBefore = new Map<number, Set<string>>();
const personalDoneBefore = new Set<string>();

// Logging out normally unmounts this, but signing in as someone else without
// it (a logout request that failed, or the login page opened while signed in)
// keeps the host and the query cache.
watch(
  () => profile.data?.id,
  (_id, previous) => {
    // The first profile to load isn't a switch.
    if (previous === undefined) return;
    organizationDoneBefore.clear();
    personalDoneBefore.clear();
    celebrated.clear();
    introDismissed.value = false;
    heldToasts.length = 0;
    for (const { id: toastId } of toasts.value) remove(toastId);
    void queryClient.resetQueries({ queryKey: tourProgressKey });
    void queryClient.resetQueries({ queryKey: setupProgressKey });
  },
);

watch(
  () => progress.data,
  (data) => {
    if (!data) return;
    const items = data.items.filter((item) => item.tier !== "tip");
    const batch: Omit<Toast, "id">[] = [];
    const before = organizationDoneBefore.get(data.organizationId);
    if (before && isAppPage.value) {
      for (const item of items) {
        const copy = SETUP_ITEM_COPY[item.key];
        const wasDone =
          item.kind === "personal"
            ? personalDoneBefore.has(item.key)
            : before.has(item.key);
        if (item.state !== "done" || wasDone || !copy) continue;
        batch.push({ title: copy.title, body: doneMessage(item) });
      }
    }
    organizationDoneBefore.set(
      data.organizationId,
      new Set(
        items
          .filter((i) => i.kind === "organization" && i.state === "done")
          .map((i) => i.key),
      ),
    );
    for (const item of items) {
      if (item.kind !== "personal") continue;
      if (item.state === "done") personalDoneBefore.add(item.key);
      else personalDoneBefore.delete(item.key);
    }
    const reached = data.level.justReached;
    if (reached && !celebrated.has(reached)) {
      levelUp.value = data.level;
    } else {
      // Celebrated in another tab: the server no longer reports it.
      if (levelUp.value && levelUp.value.justReached !== reached) {
        levelUp.value = null;
      }
      // One "Next" per batch: the same suggestion on every toast is noise.
      const last = batch.at(-1);
      if (last) last.next = nextSuggestion();
    }
    if (toastsHeld.value) {
      heldToasts.push(...batch);
    } else {
      batch.forEach(push);
    }
  },
);

// Tours only start on request, except resuming one the user left mid-way (an
// external sign-in); that waits for the level modal so the two never stack.
watch(
  () => [route.name, tour.isProgressLoaded(), !!modalContent.value] as const,
  ([, loaded, modalOpen]) => {
    if (!loaded || tour.activeTourKey.value) return;
    if (!isAppPage.value || modalOpen) return;
    tour.resumeInterrupted();
  },
  { immediate: true },
);
</script>
